import React, { useEffect, useState } from 'react';
import { Popover } from 'antd';
import cn from 'classnames';
import IconFont from '$/components/IconFont';
import type { OfficialAssetServerTagGroup } from '$/api/server.types';

import styles from './index.module.scss';
import type { OfficialAssetSelectedTags } from '../types';

const FILTER_POPOVER_Z_INDEX = 5300;

export interface TagFilterBarProps {
  tagGroups: OfficialAssetServerTagGroup[];
  selectedTags: OfficialAssetSelectedTags;
  onTagGroupChange: (groupId: string, values: string[]) => void;
  onClearTagGroup: (groupId: string) => void;
  popoverZIndex?: number;
}

/**
 * 官方资产标签筛选栏，选中值由父组件托管，支持单组多选和局部清空。
 */
function TagFilterBar({
  tagGroups,
  selectedTags,
  onTagGroupChange,
  onClearTagGroup,
  popoverZIndex = FILTER_POPOVER_Z_INDEX,
}: TagFilterBarProps): React.ReactElement {
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  /** 按 Esc 时关闭当前标签下拉，符合筛选弹层的轻量关闭预期。 */
  useEffect(() => {
    /** 监听全局键盘事件，只处理当前筛选弹层的关闭动作。 */
    const handleDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpenGroupId(null);
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, []);

  /** 清空当前标签组的已选值。 */
  const handleClearSelected = (groupId: string, event: React.MouseEvent<HTMLSpanElement>): void => {
    event.stopPropagation();
    onClearTagGroup(groupId);
  };

  /** 切换单个筛选选项的选中状态，连续多选时保持下拉展开。 */
  const handleToggleOption = (groupId: string, optionId: string): void => {
    const selectedValues = selectedTags[groupId] ?? [];
    const nextValues = selectedValues.includes(optionId)
      ? selectedValues.filter((value) => value !== optionId)
      : [...selectedValues, optionId];

    onTagGroupChange(groupId, nextValues);
  };

  return (
    <div className={styles.tagFilterBar}>
      {tagGroups.map((group) => {
        const selectedValues = selectedTags[group.key] ?? [];
        const selectedLabels = group.options
          .filter((option) => selectedValues.includes(option.value))
          .map((option) => option.name);
        const hasSelected = selectedValues.length > 0;
        const open = openGroupId === group.key;
        const dropdownContent = (
          <div className={styles.filterDropdown}>
            {group.options.map((option) => {
              const selected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={cn(styles.filterOption, { [styles.filterOptionSelected]: selected })}
                  onClick={() => handleToggleOption(group.key, option.value)}
                >
                  <span className={styles.filterOptionLabel}>{option.name}</span>
                  <span className={cn(styles.filterOptionCheckbox, { [styles.filterOptionCheckboxChecked]: selected })}>
                    {selected && <IconFont name="checkBoxMark" fontSize={10} color="currentColor" />}
                  </span>
                </button>
              );
            })}
          </div>
        );

        return (
          <Popover
            key={group.key}
            content={dropdownContent}
            open={open}
            trigger="click"
            placement="bottomLeft"
            arrowPointAtCenter={false}
            color="transparent"
            overlayClassName={cn('resetPopoverAndArrow', styles.filterPopover)}
            zIndex={popoverZIndex}
            onOpenChange={(nextOpen) => {
              setOpenGroupId((currentGroupId) => {
                if (nextOpen) {
                  return group.key;
                }
                if (currentGroupId === group.key) {
                  return null;
                }
                return currentGroupId;
              });
            }}
          >
            <button
              type="button"
              className={cn(styles.filterButton, {
                [styles.filterButtonActive]: open,
                [styles.filterButtonSelected]: hasSelected,
              })}
            >
              {hasSelected && (
                <span className={styles.filterClearIcon} onClick={(event) => handleClearSelected(group.key, event)}>
                  <IconFont name="crossBlack" fontSize={8} color="currentColor" />
                </span>
              )}
              <span className={styles.filterButtonLabel}>{group.label}</span>
              {hasSelected && (
                <>
                  <span className={styles.filterButtonDivider} />
                  <span className={styles.filterButtonValue}>{selectedLabels.join(', ')}</span>
                </>
              )}
              <IconFont
                name={open ? 'chevronUpFill' : 'chevronDownFill'}
                fontSize={8}
                color="currentColor"
                className={styles.filterButtonChevron}
              />
            </button>
          </Popover>
        );
      })}
    </div>
  );
}

export default TagFilterBar;
